import { useEffect, useRef, useState } from 'react';

import { AdornedInput, Box, FormField, FormHelp, Input, Label, Text, Textarea } from '@zillow/constellation-10';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import type { ReactNode } from 'react';

type RMWInputProps = {
  // boolean
  disabled?: boolean;
  error?: boolean;
  isRequired?: boolean;
  readOnly?: boolean;
  showOptional?: boolean;
  showCharacterCount?: boolean;

  // string
  description?: string | ReactNode;
  errorMessage?: string;
  fieldName?: string;
  flex?: string | Record<string, unknown>;
  inputType?: 'input' | 'adorned' | 'textarea';
  initialValue?: string;
  label?: string;
  leftAdornment?: string;
  maxLength?: string;
  placeholder?: string;
  rightAdornment?: string;
  textareaRows?: string;

  // function
  clearFieldError?: ((event: { target: { name: string } }) => void) | null;
  onBlur?: (value: string | { target: { name: string; value: string } }) => void;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void) | null;
  onFocus?: (event: { target: { name: string; value: string } }) => void;

  // node
  children?: ReactNode;
};

const RMWInput = ({
  disabled = false,
  error = false,
  isRequired = false,
  readOnly = false,
  showOptional = false,
  showCharacterCount = false,
  description = '',
  errorMessage = '',
  fieldName = '',
  flex = 'auto',
  inputType = 'input',
  initialValue = '',
  label = '',
  leftAdornment = '',
  maxLength = '524288',
  placeholder = '',
  rightAdornment = '',
  textareaRows = '6',
  clearFieldError = null,
  onBlur = () => {},
  onChange = null,
  onFocus = () => {},
  children = null,
}: RMWInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  const fieldNameContainsPII =
    fieldName === 'contactEmail' || fieldName === 'additionalEmail' || fieldName === 'contactName';

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // if `clearFieldError` function `null`, then focus on `errorMessage`
    if (errorMessage && !clearFieldError) {
      if (inputType === 'textarea') {
        textareaRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }
  }, [errorMessage, clearFieldError, inputType]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value);

    if (onChange) {
      onChange(e);
    }

    if (errorMessage && clearFieldError) {
      clearFieldError({ target: { name: fieldName } });
    }
  };

  const handleOnBlur = () => {
    setIsFocused(false);

    if (fieldName) {
      onBlur({ target: { name: fieldName, value } });

      return;
    }

    onBlur(value);
  };

  const handleOnFocus = () => {
    setIsFocused(true);
    onFocus({ target: { name: fieldName, value } });
  };

  const getInputProps = () => ({
    ref: inputRef,
    disabled: readOnly || disabled,
    placeholder: placeholder,
    value: value,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    maxLength: parseInt(maxLength, 10),
  });

  const maxLengthNumber = parseInt(maxLength, 10);
  const hasDescriptionText = description !== undefined && description !== '';

  const renderDescription = () => {
    if (errorMessage) {
      return (
        <FormHelp>
          <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
        </FormHelp>
      );
    }

    if (showCharacterCount) {
      // CharacterCountFormHelp returns null unless the counter shows or it has helper text.
      const isCounterVisible = isFocused || value.length > maxLengthNumber;

      if (!isCounterVisible && !hasDescriptionText) {
        return undefined;
      }

      return (
        <CharacterCountFormHelp
          currentLength={value.length}
          maxLength={maxLengthNumber}
          isFocused={isFocused}
          helperText={description}
          label={label}
        />
      );
    }

    return hasDescriptionText ? <FormHelp>{description}</FormHelp> : undefined;
  };

  const getTextareaProps = () => ({
    ref: textareaRef,
    disabled: readOnly || disabled,
    placeholder: placeholder,
    value: value,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    maxLength: parseInt(maxLength, 10),
  });

  return (
    <Box css={{ flex: flex }}>
      <FormField
        error={!!errorMessage || error}
        disabled={disabled || readOnly}
        required={isRequired && !disabled && !readOnly}
        label={
          label && (
            <Label>
              {label}
              {showOptional && (
                <>
                  {' '}
                  <Text textStyle="body-sm">(optional)</Text>
                </>
              )}
            </Label>
          )
        }
        control={
          <>
            {inputType === 'input' && <Input {...getInputProps()} className={fieldNameContainsPII ? 'fs-mask' : ''} />}

            {inputType === 'adorned' && (
              <AdornedInput
                startAdornment={leftAdornment && <AdornedInput.Adornment>{leftAdornment}</AdornedInput.Adornment>}
                input={<AdornedInput.Input {...getInputProps()} />}
                endAdornment={rightAdornment && <AdornedInput.Adornment>{rightAdornment}</AdornedInput.Adornment>}
              />
            )}

            {inputType === 'textarea' && (
              <Textarea rows={parseInt(textareaRows, 10)} css={{ resize: 'vertical' }} {...getTextareaProps()} />
            )}

            {children}
          </>
        }
        description={renderDescription()}
      />
    </Box>
  );
};

export default RMWInput;
